import { ReactNode } from 'react';
import { startCase } from 'lodash';
import styled from 'styled-components';

import { simpleDate } from 'lib/date';
import Workflow, { WorkflowAction } from 'models/Workflow';

import { FieldLabel, FieldValue } from './categoryCard/styles';
import { labelForValue } from './actionConfig';
import { VALUE_NAMES } from './constants';
import { coverageLabel, COVERAGES_VALUE, currentCoverages, ExtractedCoverage } from './coverages';
import RevealableSsn from './RevealableSsn';
import { maskSsn } from './ssn';

// The demographics the AI landed, shown inside the completed Extract Demographics action.
//
// The LAYOUT is declared here (UX, 2026-08-04): who the patient is on one row, what they're
// insured by on the next. The DATA still comes from the action's own `produces` — so this is a
// spec applied to the API's list, not a replacement for it. Anything produced that the spec
// doesn't mention renders in a trailing row rather than disappearing, which is what keeps a value
// added to `produces` later from silently vanishing off this card.

// One field as rendered: a label and one or more lines under it. `node` overrides the lines for
// the one value that isn't plain text — the SSN, which carries its own reveal control.
interface Cell {
  key: string;
  label: string;
  lines: string[];
  node?: ReactNode;
}

// Abbreviated on this card only. The API's value catalog is the label source everywhere else
// (OLIO-2574) and stays untouched — renaming `patient_dob` to "DOB" in the catalog would change
// it on every other surface too, which UX asked for here and nowhere else.
const CARD_LABELS: Record<string, string> = {
  [VALUE_NAMES.PATIENT_DOB]: 'DOB',
  [VALUE_NAMES.PATIENT_GENDER]: 'Gender',
  [VALUE_NAMES.PATIENT_SSN]: 'SSN',
  // Only older referrals still carry this — referral intake stopped capturing it in OLIO-2890.
  // Labelled "Plan Type" rather than the catalog's "Payer Name" because it sits directly under
  // the per-coverage "Payer Name (Primary)" holding a *different* string, and because what it
  // held was payer and plan together.
  [VALUE_NAMES.PLAN_TYPE]: 'Plan Type',
};

// The patient's name is shown as one field, but it's stored as two values — so it's composed
// here and the two name values are consumed by it rather than rendered on their own.
const NAME_VALUES: string[] = [VALUE_NAMES.PATIENT_FIRST_NAME, VALUE_NAMES.PATIENT_LAST_NAME];

// Row 1 after the composed name, in this order. Row 2 is the insurance row, row 3 the source.
const PATIENT_ROW: string[] = [VALUE_NAMES.PATIENT_DOB, VALUE_NAMES.PATIENT_GENDER, VALUE_NAMES.PATIENT_SSN];
const INSURANCE_ROW: string[] = [COVERAGES_VALUE, VALUE_NAMES.PLAN_TYPE];
// Placed deliberately rather than left to the catch-all unplaced row below, now that resolving it
// makes it a reported dimension (OLIO-3158). Its own row rather than an appendix to the insurance
// one: where a referral came from is not an insurance detail, and it is what a coordinator scans
// for when triaging.
const SOURCE_ROW: string[] = [VALUE_NAMES.REFERRAL_SOURCE];

function scalarLines(name: string, value: unknown): string[] {
  if (value == null || value === '') return [];
  // A produced value this card has no rendering for is SKIPPED, not stringified. `produces` comes
  // from the API's published contract, so api can start producing a new compound value at any
  // time — and does, ahead of the web deploy that teaches this card to render it. Without this
  // guard that window is a broken page: an array or object reaching JSX throws and the error
  // boundary swallows the whole referral (which is exactly what happened with `coverages` on
  // 2026-08-04). Skipping means the value is invisible until the web side ships, which is the
  // right failure: nothing is claimed that isn't understood.
  if (typeof value === 'object') return [];
  if (name === VALUE_NAMES.PATIENT_SSN) return [maskSsn(String(value))];
  if (name === VALUE_NAMES.PATIENT_DOB) return [simpleDate(String(value)) ?? String(value)];
  if (name === VALUE_NAMES.PATIENT_GENDER) return [startCase(String(value))];
  return [String(value)];
}

// Each coverage becomes its own pair of columns — "Payer Name (Primary)", "Member ID (Primary)" —
// rather than one packed line, so the row scans across like the rest of the card. The suffix comes
// from the coverage's own sequence, so a third one needs no change here.
// Takes coverages already narrowed by currentCoverages — a raw cast would let a null or non-object
// entry reach coverageLabel, which reads `.sequence` off it and throws mid-render with nothing
// above the referral page to catch it.
function coverageCells(coverages: ExtractedCoverage[]): Cell[] {
  return coverages.flatMap((coverage, index) => {
    const label = coverageLabel(coverage);
    const suffix = label ? ` (${label})` : '';
    return [
      { key: `payer-${index}`, label: `Payer Name${suffix}`, lines: [coverage.payerName ?? ''] },
      { key: `member-${index}`, label: `Member ID${suffix}`, lines: [coverage.memberId ?? ''] },
    ].filter((cell) => cell.lines[0] !== '');
  });
}

export default function ExtractionResults({ action, workflow }: { action: WorkflowAction; workflow: Workflow }) {
  const read = (name: string) => workflow.valueFor(name)?.value;

  const cellFor = (name: string): Cell[] => {
    // The referral's LIVE coverages, not the extraction snapshot the value itself holds — this card
    // is where "Edit demographics" lives, and an insurance corrected or added there has to show
    // here, as the rest of the card already does for an edited DOB or name (OLIO-3332). The same
    // facets and coverage-scoped values the header chips read, so a payer shows on both or neither.
    if (name === COVERAGES_VALUE) return coverageCells(currentCoverages(workflow));
    const lines = scalarLines(name, read(name));
    if (!lines.length) return [];

    const label = CARD_LABELS[name] ?? labelForValue(workflow, name);
    // The SSN renders through the shared reveal control rather than as text — same component the
    // header uses, so masked-by-default holds identically on both.
    const node = name === VALUE_NAMES.PATIENT_SSN ? <RevealableSsn ssn={String(read(name))} /> : undefined;
    return [{ key: name, label, lines, node }];
  };

  const produced = action.produces;
  const inSpec = [...NAME_VALUES, ...PATIENT_ROW, ...INSURANCE_ROW, ...SOURCE_ROW];

  const nameCell: Cell[] =
    produced.some((name) => NAME_VALUES.includes(name)) && workflow.hasPatientName
      ? [{ key: 'patient-name', label: 'Name', lines: [workflow.patientName] }]
      : [];

  const rows = [
    [...nameCell, ...PATIENT_ROW.filter((name) => produced.includes(name)).flatMap(cellFor)],
    INSURANCE_ROW.filter((name) => produced.includes(name)).flatMap(cellFor),
    SOURCE_ROW.filter((name) => produced.includes(name)).flatMap(cellFor),
    // Produced but unplaced — rendered rather than dropped, so nothing goes missing unnoticed.
    produced.filter((name) => !inSpec.includes(name)).flatMap(cellFor),
  ].filter((row) => row.length > 0);

  if (rows.length === 0) return null;

  return (
    <Groups data-testid='extraction-results'>
      {rows.map((row) => (
        <Row key={row[0].key}>
          {row.map((cell) => (
            <Field key={cell.key}>
              <FieldLabel>{cell.label}</FieldLabel>
              {cell.node ? (
                <FieldValue>{cell.node}</FieldValue>
              ) : (
                cell.lines.map((line) => <FieldValue key={line}>{line}</FieldValue>)
              )}
            </Field>
          ))}
        </Row>
      ))}
    </Groups>
  );
}

const Groups = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 8px 0;
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px 24px;
`;

const Field = styled.div`
  min-width: 120px;
`;
